import { UserIdSchema } from "@core/domains/identity";
import {
  OrderKeySchema,
  createModel,
  createModelId,
  createModelSchemas,
  generateOrderKey,
  recoverOrderKeyFromId,
} from "@core/foundation/model";
import { z } from "@core/zod";

import { GridEpochIdSchema } from "./epoch.ts";
import { CategorySchema } from "./shared/category.ts";
import { GlyphSchema } from "./shared/glyph.ts";
import { ID_PREFIX } from "./shared/id-prefix.ts";

const LogEntryCellSchema = z.object(
  { col: z.int().nonnegative(), date: z.iso.datetime() },
  {
    col: "The grid column the cell sits in, counted from zero.",
    date: "The day whose grid row the cell sits in.",
  },
);

export interface LogEntryCell extends z.infer<typeof LogEntryCellSchema> {}

const LogEntryReferenceSchema = z.discriminatedUnion("type", [
  z.object(
    { barcode: z.alphaNumeric(), type: z.literal("barcode") },
    {
      barcode: "The barcode of the scanned product.",
      type: "The reference's type, a product found by its barcode.",
    },
  ),
  z.object(
    { type: z.literal("manual") },
    { type: "The reference's type, a product the user entered by hand." },
  ),
]);

export type LogEntryReference = z.infer<typeof LogEntryReferenceSchema>;

const LogEntryStatusSchema = z.enum(["dropped", "pending", "softDeleted"]);

const { generateId: generateLogEntryId, idSchema: LogEntryIdSchema } =
  createModelId({ idPrefix: ID_PREFIX.LOG_ENTRY });

export type LogEntryId = z.infer<typeof LogEntryIdSchema>;

export const LogEntry = createModel({
  create: (item, schema) => {
    return {
      ...schema.decode(item),
      // oxlint-disable-next-line no-warning-comments
      // TODO(APT-225): resolve category and glyph from the product.
      category: pickRandom(CategorySchema.options, "eating-out"),
      glyph: pickRandom(GlyphSchema.options, "beef"),
      id: generateLogEntryId(),
      orderKey: generateOrderKey(),
    };
  },
  schemas: createModelSchemas({
    base: z.object(
      {
        category: CategorySchema,
        cells: z.array(LogEntryCellSchema).nullable(),
        glyph: GlyphSchema,
        gridEpochId: GridEpochIdSchema,
        id: LogEntryIdSchema,
        name: z.string(),
        orderKey: OrderKeySchema,
        product: LogEntryReferenceSchema,
        status: LogEntryStatusSchema,
        totalCalories: z.number().nonnegative(),
        userId: UserIdSchema,
        weightGrams: z.number().positive().nullable(),
      },
      {
        category: "The category the entry's color stands for.",
        cells: "The grid cells the entry occupies, or null until placed.",
        glyph: "The glyph the entry's figure carries.",
        gridEpochId: "The grid epoch the entry was logged in.",
        id: "The unique identifier for the entry.",
        name: "The name of the logged product.",
        orderKey: "The key ordering the entries by creation, and nothing else.",
        product: "The product the entry was logged from.",
        status: "The entry's status: dropped, pending, or softDeleted.",
        totalCalories: "The entry's total kcal.",
        userId: "The id of the user the entry belongs to.",
        weightGrams: "The logged weight in grams, or null when unknown.",
      },
    ),
    defaults: { status: "dropped" },
    derive: {
      create: schema =>
        schema.omit({ category: true, glyph: true, id: true, orderKey: true }),
      // oxlint-disable-next-line no-warning-comments
      // TODO(APT-2405): drop once the stored records carry their orderKey.
      recovery: recoverOrderKeyFromId,
      update: schema => schema.pick({ cells: true, status: true }).partial(),
    },
  }),
  update: (item, update, schema) => {
    return { ...item, ...schema.decode(update) };
  },
});

export interface LogEntry extends z.infer<typeof LogEntry.schema> {}

function pickRandom<Item>(items: readonly Item[], fallback: Item): Item {
  return items[Math.floor(Math.random() * items.length)] ?? fallback;
}
